import { Mail, Phone, User } from 'lucide-react';
import type { AppointmentAttendanceHint } from '@/types/dynamic-form';
import { cn } from '@/lib/utils';

export function formatPhoneDisplay(phone: unknown): string {
  const raw = String(phone ?? '').trim();
  if (!raw) return '';
  const d = raw.replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d.startsWith('1')) return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return raw;
}

export function telHref(phone: unknown): string {
  const d = String(phone ?? '').replace(/\D/g, '');
  return d ? `tel:${d}` : '';
}

export function StudentContactSection({
  name,
  email,
  phone,
  compact,
}: {
  name: string;
  email: string;
  phone: string;
  compact?: boolean;
}) {
  const phoneStr = phone.trim();
  const phoneDisplay = phoneStr ? formatPhoneDisplay(phoneStr) : '';
  const tel = telHref(phoneStr);
  const emailTrim = email.trim();
  const iconCls = cn('mt-0.5 shrink-0 text-muted-foreground', compact ? 'h-3.5 w-3.5' : 'h-4 w-4');
  const labelCls = 'text-[10px] font-medium uppercase tracking-wide text-muted-foreground';
  const valueCls = cn('mt-0.5 break-words font-medium leading-snug', compact ? 'text-xs' : 'text-sm');
  const cellBox = cn(
    'flex min-w-0 gap-2 rounded-md border border-border/50 bg-background/70 px-2.5 py-2',
    compact && 'px-2 py-1.5'
  );

  return (
    <div
      className={cn(
        'rounded-lg border border-border/60 bg-muted/10',
        compact ? 'px-2 py-2' : 'px-3 py-3'
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Student</p>
      <div
        className={cn(
          'mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3',
          compact ? 'mt-1.5 gap-1.5 sm:gap-2' : 'mt-2'
        )}
      >
        <div className={cellBox}>
          <User className={iconCls} aria-hidden />
          <div className="min-w-0 flex-1">
            <p className={labelCls}>Name</p>
            <p className={cn(valueCls, 'text-foreground')}>{name.trim() || '—'}</p>
          </div>
        </div>
        <div className={cellBox}>
          <Mail className={iconCls} aria-hidden />
          <div className="min-w-0 flex-1">
            <p className={labelCls}>Email</p>
            <div className={valueCls}>
              {emailTrim ? (
                <a
                  href={`mailto:${emailTrim}`}
                  className="text-primary underline-offset-2 hover:underline break-all"
                >
                  {emailTrim}
                </a>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
          </div>
        </div>
        <div className={cellBox}>
          <Phone className={iconCls} aria-hidden />
          <div className="min-w-0 flex-1">
            <p className={labelCls}>Mobile</p>
            <div className={valueCls}>
              {phoneDisplay ? (
                tel ? (
                  <a href={tel} className="text-primary underline-offset-2 hover:underline break-all">
                    {phoneDisplay}
                  </a>
                ) : (
                  <span className="text-foreground">{phoneDisplay}</span>
                )
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AttendanceMarkHint({ att, canMark }: { att: AppointmentAttendanceHint; canMark: boolean }) {
  if (canMark) return null;
  if (att.code === 'pending') {
    return (
      <p className="max-w-md text-xs leading-snug text-muted-foreground">
        <span className="font-medium text-foreground">Attended</span> and{' '}
        <span className="font-medium text-foreground">Not attended</span> unlock after this slot&apos;s scheduled start time
      </p>
    );
  }
  return (
    <p className="max-w-xs text-xs leading-snug text-muted-foreground">No attendance changes are available for this row.</p>
  );
}

export function attendanceBadgeClass(code: string) {
  switch (code) {
    case 'attended':
      return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300';
    case 'not_attended':
      return 'border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200';
    case 'n_a':
      return 'border-muted-foreground/30 bg-muted text-muted-foreground';
    default:
      return 'border-border/60 bg-muted/30';
  }
}
